// What you are meant to be doing, and how far along you are (PLAN §12.1.1).
//
// The model behind the objectives panel, which read the run itself until
// day 6 of the client split. **Only the objective you are on and the ones
// behind you**: a list that shows what is coming turns a sequence into a
// checklist, and the next one is usually a spoiler for the region it opens.

import { activeObjective, inOrder } from "src/engine/rules/campaign/objectives";
import type { GameState } from "src/engine/rules/commands";

export interface ObjectiveLine {
  id: string;
  text: string;
  done: boolean;
  current: boolean;
  /** Counted progress. `need` is 1 for anything that is not a delivery. */
  have: number;
  need: number;
}

export interface ObjectivesModel {
  level: string;
  lines: ObjectiveLine[];
}

/**
 * Null outside a run, and **null with nothing left to do**: the panel is an
 * instruction, so with no active objective it has none to give. A cleared
 * front and a won run announce themselves elsewhere.
 */
export function objectivesModel(state: GameState): ObjectivesModel | null {
  const level = state.run?.level;
  const progress = state.run?.objectives;
  if (!level || !progress) return null;
  const active = activeObjective(level, progress);
  if (!active) return null;

  const lines: ObjectiveLine[] = [];
  for (const objective of inOrder(level)) {
    const at = progress.find((one) => one.id === objective.id);
    const done = at?.done ?? false;
    const current = objective.id === active.id;
    if (!done && !current) continue;
    const need = objective.kind === "deliver" ? (objective.count ?? 1) : 1;
    lines.push({
      id: objective.id,
      text: objective.text,
      done,
      current,
      have: Math.min(need, at?.progress ?? 0),
      need
    });
  }
  return { level: level.levelName, lines };
}
